import {css, useTheme} from '@mui/material';
import {ConfigObjectType, GetStylesFunction, useDefaultScroll, useLocalStorage} from '@wallaby-ui/core';
import React, {useMemo, useRef} from 'react';
import {useFormatters} from 'src/js/common';
import {HaidiSchema} from 'src/js/common/types';

import {documentContainer} from '../..';

interface OwnProps {
  encounterData:
    | HaidiSchema.components['schemas']['EncounterPropertiesDto']
    | undefined;
  encounterId: string;
  documentId: string;
  config: ConfigObjectType;
}

export type Props = OwnProps;

export const EncounterDetailProperties: React.FC<Props> = (props) => {
  const {encounterData, encounterId, documentId, config} = props;

  const {rawDocumentData} = documentContainer.useLoadDocument({
    documentId,
    encounterId,
    config,
    isHaidi: true,
  });
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const {t, tEnum, formatDateTime} = useFormatters();
  const empty = t('label.data.notAvailable');
  const document = useMemo(
    () =>
      rawDocumentData as HaidiSchema.components['schemas']['DocumentViewDto'],
    [rawDocumentData]
  );
  const patient = useMemo(
    () => ({
      id: encounterData?.patientId ?? document?.patientProperties?.patientId,
      srcId:
        encounterData?.patientSrcId ??
        document?.patientProperties?.patientSrcId,
      identificationNumber:
        encounterData?.identificationNumber ??
        document?.patientProperties?.patientIdentificationNumber,
      fullName:
        encounterData?.fullName ?? document?.patientProperties?.patientFullName,
    }),
    [encounterData, document]
  );
  const scrollRef = useRef(null as HTMLDivElement | null);
  const [propertiesScroll, setPropertiesScroll] = useLocalStorage(
    `/encounter/${encounterId}/document/${documentId}.propertiesScroll`,
    0
  );
  useDefaultScroll(scrollRef, {to: propertiesScroll, dependOn: propertiesScroll});
  return (
    <div
      css={muiStyles.overflowWrapper}
      ref={scrollRef}
      onScroll={(event) => {
        setPropertiesScroll((event.target as HTMLDivElement).scrollTop);
      }}
    >
      <div css={muiStyles.wrapper}>
        {patient.id && (
          <div css={muiStyles.section}>
            <div css={muiStyles.sectionHeader}>
              {t('encounter.properties.patient')}
            </div>
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.patient.name')}
              value={String(patient.fullName || empty)}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.patient.srcId')}
              value={String(patient.srcId || empty)}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.patient.id')}
              value={String(patient.id || empty)}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.patient.birthNumber')}
              value={String(patient.identificationNumber || empty)}
              muiStyles={muiStyles}
            />
          </div>
        )}
        {encounterId && (
          <div css={muiStyles.section}>
            <div css={muiStyles.sectionHeader}>
              {t('encounter.properties.encounter')}
            </div>
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.encounter.type')}
              value={
                encounterData?.encounterType
                  ? tEnum('EncounterTypeEnum', encounterData?.encounterType)
                  : empty
              }
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.encounter.department')}
              value={String(encounterData?.admissionDepartment || empty)}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.encounter.start')}
              value={
                encounterData?.encounterStartedAt
                  ? formatDateTime(encounterData?.encounterStartedAt)
                  : empty
              }
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.encounter.end')}
              value={
                encounterData?.encounterEndedAt
                  ? formatDateTime(encounterData?.encounterEndedAt)
                  : empty
              }
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.encounter.srcId')}
              value={String(encounterData?.encounterSrcId || empty)}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.encounter.id')}
              value={String(encounterData?.encounterId ?? empty)}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.encounter.escort')}
              value={t(
                `global.${encounterData?.encounterIsEscort ? 'yes' : 'no'}`
              )}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.encounter.deleted')}
              value={t(
                `global.${encounterData?.encounterIsDeleted ? 'yes' : 'no'}`
              )}
              muiStyles={muiStyles}
            />
          </div>
        )}
        {documentId && rawDocumentData && (
          <div css={muiStyles.section}>
            <div css={muiStyles.sectionHeader}>
              {t('encounter.properties.document')}
            </div>
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.document.type')}
              value={
                rawDocumentData?.documentProperties.documentType
                  ? tEnum(
                      'DocumentTypeEnum',
                      rawDocumentData?.documentProperties.documentType
                    )
                  : empty
              }
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.document.name')}
              value={String(
                rawDocumentData?.documentProperties.documentName || empty
              )}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.document.departmentOrigin')}
              value={String(
                rawDocumentData?.documentProperties
                  .documentOrganizationFullLabel || empty
              )}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.document.createdTimestamp')}
              value={
                rawDocumentData?.documentProperties.documentTimestamp
                  ? formatDateTime(
                      rawDocumentData.documentProperties.documentTimestamp
                    )
                  : empty
              }
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.document.versionTimestamp')}
              value={
                rawDocumentData?.documentProperties.versionTimestamp
                  ? formatDateTime(
                      rawDocumentData.documentProperties.versionTimestamp
                    )
                  : empty
              }
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.document.srcId')}
              value={String(
                rawDocumentData?.documentProperties.documentSrcId || empty
              )}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.document.wallabyId')}
              value={String(
                rawDocumentData?.documentProperties.documentId || empty
              )}
              muiStyles={muiStyles}
            />
            <EncounterDetailPropertiesRow
              label={t('encounter.properties.document.versionId')}
              value={String(
                rawDocumentData?.documentProperties.versionId || empty
              )}
              muiStyles={muiStyles}
            />
          </div>
        )}
      </div>
    </div>
  );
};

EncounterDetailProperties.displayName = 'EncounterDetailProperties';

type RowProps = {
  label: string;
  value: string;
  muiStyles: any;
};
const EncounterDetailPropertiesRow: React.FC<RowProps> = (props) => {
  const {label, value, muiStyles} = props;
  return (
    <div css={muiStyles.sectionRow}>
      <span css={muiStyles.sectionRowLabel}>{label}</span>
      <span css={muiStyles.sectionRowValue}>{value}</span>
    </div>
  );
};
EncounterDetailPropertiesRow.displayName = 'EncounterDetailPropertiesRow';

const getStyles: GetStylesFunction = (options) => {
  return {
    overflowWrapper: css`
      height: 100%;
      background: var(--dlBackgroundPanelColor);
      padding: 16px;
      overflow-y: auto;
    `,
    wrapper: css`
      display: flex;
      flex-direction: column;
      gap: 16px;
    `,
    section: css`
      display: flex;
      flex-direction: column;
      border-radius: 8px;
      overflow: hidden;
    `,
    sectionHeader: css`
      background: var(--dlThemeGray-300);
      padding: 8px;
      font-family: var(--dlFontFamilySemiBold);
      font-size: 14px;
      line-height: 16px;
      color: var(--dlTheme-700);
    `,
    sectionRow: css`
      background: var(--dlThemeGray-100);

      &:nth-of-type(2n) {
        background: #fff;
      }

      padding: 8px;
      display: flex;
      justify-content: space-between;
      font-family: var(--dlFontFamilyNormal);
      font-size: 14px;
      line-height: 16px;
    `,
    sectionRowLabel: css`
      color: var(--dlTheme-700);
    `,
    sectionRowValue: css`
      color: var(--dlThemeGray-500);
      text-align: end;
    `,
  };
};
